import {
  Badge,
  Box,
  Card,
  Group,
  Loader,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Title as ChartTitle,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import isBetween from "dayjs/plugin/isBetween";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";

// Register chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend,
);

// Register dayjs plugins
dayjs.extend(isBetween);
dayjs.locale("fr");

export const HourlyMetricCard = ({ title, value, subtitle, icon, color }) => (
  <Card padding="md" radius="md" withBorder>
    <Group position="apart" mb="xs">
      <ThemeIcon color={color} variant="light" size="lg">
        {icon}
      </ThemeIcon>
    </Group>
    <Text size="xs" color="dimmed" transform="uppercase" weight={600} mb={4}>
      {title}
    </Text>
    <Text size="xl" weight={700} mb={4}>
      {value}
    </Text>
    {subtitle && (
      <Text size="xs" color="dimmed">
        {subtitle}
      </Text>
    )}
  </Card>
);

export const useHourlyUsageData = (
  parentsData,
  dateRange,
  provenanceFilter = "all",
) => {
  // Data processing for App Usage by Hour Chart
  const hourlyUsageData = useMemo(() => {
    const { start, end } = dateRange;
    const hourCounts = new Array(24).fill(0);
    let totalUsage = 0;

    if (parentsData?.data) {
      parentsData.data.forEach((parent) => {
        // Filtrer par provenance (comparaison insensible à la casse)
        if (
          provenanceFilter !== "all" &&
          (!parent.provenance ||
            parent.provenance.toLowerCase() !== provenanceFilter.toLowerCase())
        ) {
          return;
        }
        const usageDate = parent.last_credit_usage;
        if (!usageDate) return;

        const date = dayjs(usageDate);
        if (!date.isValid()) return;

        // Check if the usage is within the selected date range
        if (date.isBetween(start, end, "day", "[]")) {
          const hour = date.hour();
          hourCounts[hour]++;
          totalUsage++;
        }
      });
    }

    // Generate labels for 24 hours
    const labels = Array.from({ length: 24 }, (_, i) => `${i}h`);

    // Trouver l'heure de pic
    const peakHour = hourCounts.indexOf(Math.max(...hourCounts));
    const peakCount = hourCounts[peakHour];

    // Calculer les utilisations par plage horaire
    const morningUsage = hourCounts.slice(6, 12).reduce((a, b) => a + b, 0); // 6h-12h
    const afternoonUsage = hourCounts.slice(12, 18).reduce((a, b) => a + b, 0); // 12h-18h
    const eveningUsage = hourCounts.slice(18, 24).reduce((a, b) => a + b, 0); // 18h-24h
    const nightUsage = hourCounts.slice(0, 6).reduce((a, b) => a + b, 0); // 0h-6h

    return {
      labels,
      chartValues: hourCounts,
      totalUsage,
      peakHour,
      peakCount,
      morningUsage,
      afternoonUsage,
      eveningUsage,
      nightUsage,
    };
  }, [dateRange, parentsData, provenanceFilter]);

  return hourlyUsageData;
};

const AppUsageByHourChart = ({
  parentsData,
  isLoading,
  dateRange,
  showCards = true,
  provenanceFilter = "all",
}) => {
  const hourlyUsageData = useHourlyUsageData(
    parentsData,
    dateRange,
    provenanceFilter,
  );

  // Chart data configuration
  const chartConfig = {
    labels: hourlyUsageData.labels,
    datasets: [
      {
        label: "Utilisations",
        data: hourlyUsageData.chartValues,
        backgroundColor: "rgba(59, 130, 246, 0.6)",
        borderColor: "#3b82f6",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: { size: 13 },
        bodyFont: { size: 13 },
        displayColors: false,
        callbacks: {
          title: (context) => `${context[0].label}`,
          label: (context) => `${context.parsed.y} utilisations`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: {
          color: "#9ca3af",
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#f3f4f6", borderDash: [5, 5], drawBorder: false },
        ticks: {
          stepSize: 1,
          color: "#9ca3af",
          precision: 0,
        },
      },
    },
    maintainAspectRatio: false,
  };

  return (
    <Stack spacing="md">
      {/* Graphique */}
      <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
        <Group position="apart" mb="md">
          <Text size="lg" weight={700}>
            Utilisation par heure
          </Text>
          <Group spacing="xs">
            <Badge variant="light" color="violet" size="md">
              Pic: {hourlyUsageData.peakHour}h
            </Badge>
            <Badge variant="light" color="blue" size="md">
              {hourlyUsageData.peakCount} utilisations
            </Badge>
          </Group>
        </Group>

        <div style={{ height: "300px" }}>
          {isLoading ? (
            <Box
              h="100%"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Loader size="lg" />
            </Box>
          ) : (
            <Bar options={chartOptions} data={chartConfig} />
          )}
        </div>

        <Text
          align="center"
          color="dimmed"
          size="xs"
          mt="md"
          transform="uppercase"
          weight={600}
          letterSpacing={0.5}
        >
          Distribution horaire (0h - 23h)
        </Text>
      </Card>
    </Stack>
  );
};

export default AppUsageByHourChart;
